import React from "react";
import * as m from "../../lib/model";
import { Card } from "../shared";
import { ExternalLink } from "lucide-react";
import { filled } from "./deck";

const short = (t, n) => (t && String(t).length > n ? String(t).slice(0, n - 1).trimEnd() + "…" : t || "");
const stripRefs = (t) => String(t || "").replace(/\s*\[\d+\]/g, "").trim();
const yr = (v) => String(v || "").match(/\d{4}/)?.[0] || "";

const url = (v) => (/^https?:\/\//i.test(String(v || "")) ? String(v) : "");
const citation = (note, cits, i) => {
  const ref = String(note || "").match(/\[(\d+)\]/);
  const cit = (ref && cits[+ref[1] - 1]) || cits[i];
  return cit ? { url: url(cit.url), title: cit.title || cit.url } : null;
};
const source = (c, cits, i) => {
  const direct = url(c.url || c.sourceUrl || c.source);
  if (direct) return { url: direct, title: c.sourceTitle || "Read the announcement" };
  const cit = citation(c.note || c.recentDeals, cits, i);
  return cit && cit.url ? { url: cit.url, title: `Cited source: ${cit.title}` } : null;
};

const fromMarket = (market = {}) => {
  const cits = Array.isArray(market.compCitations) ? market.compCitations : [];
  const cons = (Array.isArray(market.consolidators) ? market.consolidators : []).filter((c) => c.name && (c.recentDeals || c.appetite));
  const comps = (Array.isArray(market.comps) ? market.comps : []).filter((c) => c.descriptor || c.name);
  const rows = cons.slice(0, 4).map((c, i) => ({
    date: yr(c.recentDeals) || "Recent",
    buyer: c.name,
    target: short(stripRefs(c.recentDeals) || "Multiple add-on acquisitions", 70),
    geography: c.hq || "—",
    read: short(stripRefs(c.note || c.appetite), 110),
    src: source(c, cits, i),
  }));
  comps.slice(0, 6 - rows.length).forEach((c, i) => rows.push({
    date: yr(c.year) || "—",
    buyer: c.buyer || "Regional consolidator",
    target: short(stripRefs(c.descriptor || c.name), 70),
    geography: c.location || "—",
    read: short(stripRefs(c.note) || `${c.multiple ? `Traded at ${c.multiple} — ` : ""}validates entry pricing in the corridor`, 110),
    src: source(c, cits, i),
  }));
  return rows;
};

// Same rows the room table uses, so the pitchbook slide can never disagree with the room.
export const precedentRows = (market, customRows) => {
  const custom = filled(customRows, "buyer");
  return custom.length ? custom.map((r) => ({ date: r.date || "—", buyer: r.buyer, target: r.target || "—", geography: r.geography || "—", read: r.read || "" })) : fromMarket(market || {});
};

export function PrecedentBlock({ M, market, block = {}, firm }) {
  const custom = filled(block.rows, "buyer");
  const rows = custom.length ? custom : fromMarket(market || {});
  if (!rows.length) return null;
  const region = M.address ? M.address.split(",").pop().trim() : "";
  const headline = block.headline || `The playbook is being run — successfully${region ? ` — in ${region}` : ""}`;
  const soWhat = block.soWhat || `Institutional consolidators are already paying platform multiples for the same assets ${firm?.name || "we"} can buy at ${m.fmtX(m.entryMultiple(M))} — and ${firm?.name || "the sponsor"}'s founder-operator credibility with seller counterparts is an edge those buyers cannot easily match: the "one of us" close.`;
  return (
    <div className="ir-gap" data-testid="ir-precedent">
      <div className="ir-deck-head">
        <span className="ir-eyebrow">Precedent playbook translation</span>
        <h2 className="ir-deck-h" data-testid="ir-precedent-headline">{headline}</h2>
      </div>
      <Card title="Precedent transactions" className="table-scroll" testid="ir-precedent-table">
        <table className="tbl">
          <thead><tr><th>Date</th><th>Buyer</th><th>Target</th><th>Geography</th><th>Strategic read</th><th className="num">Source</th></tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const s = r.src || (url(r.source) ? { url: url(r.source), title: "Read the announcement" } : null);
              return (
                <tr key={i} data-testid={`ir-precedent-row-${i}`}>
                  <td className="mono">{r.date || "—"}</td>
                  <td><b>{r.buyer}</b></td>
                  <td>{r.target || "—"}</td>
                  <td>{r.geography || "—"}</td>
                  <td>{r.read || "—"}</td>
                  <td className="num">{s ? <a className="mk-src" href={s.url} target="_blank" rel="noreferrer" title={s.title} data-testid={`ir-precedent-src-${i}`}><ExternalLink size={13} /></a> : <span className="mk-src off">—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="ir-foot-note">Precedent activity compiled from public announcements and market research; multiples where disclosed. Not an indication of pricing for this offering.</div>
      </Card>
      <div className="ir-band ir-gap" data-testid="ir-precedent-sowhat">
        <span className="mk-panel-k">{block.soWhatTitle || "So what"}</span>
        <p>{soWhat}</p>
      </div>
    </div>
  );
}
